import React from 'react'
import { logger } from '../logging'
import "flag-icons/css/flag-icons.min.css"

import { MyTmpVpnClient } from '@mytmpvpn/mytmpvpn-client'
import { Vpn, VpnState } from '@mytmpvpn/mytmpvpn-common/models/vpn'
import { MainTitle, PageContainer, reactUpdateVpnWithMetrics, computeEstimatedCost, computeEstimatedRuntime, convertSecondsToHMSShort, getConfiguredMaxPeanuts, HelpHelper } from '../components/Common'
import { useErrorContext } from '../ErrorContext'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { VpnWithMetrics } from './Home'
import { Button, Table, TableHead, TableRow, TableCell, TableBody, View, Loader, Alert, Input, Text, Flex, Link, Card, Placeholder } from '@aws-amplify/ui-react'
import { InfrastructureProvider, LocationWithProvidersInfo, PricingRate } from '@mytmpvpn/mytmpvpn-common/models/location'
import { VpnConfig, VpnConfigLimits } from '@mytmpvpn/mytmpvpn-common/models/vpnConfig'
import { Location } from '@mytmpvpn/mytmpvpn-common/models/location'
import { isClientAuthenticated } from '../utils/authUtils'
import { City } from '../components/City'
import { Country } from '../components/Country'
import { useComponentSize } from '../components/ResponsiveSize'

async function createVpn(client: MyTmpVpnClient,
    location: Location,
    setLocationMsg: any,
    config: VpnConfig,
    setVpnsWithMetrics: any,
    setError: any,
    setLoading: any) {

    if (!isClientAuthenticated(client)) {
        setError(new Error('Client not authenticated'))
        return
    }

    try {
        logger.log(`Setting inProgress to true for ${JSON.stringify(location)}`)
        setLoading(true)

        const vpn = await client.createVpn(location.geonamesId, config)
        logger.log(`New vpn created: ${JSON.stringify(vpn)}`)

        setVpnsWithMetrics((prevVpns: VpnWithMetrics[]) => {
            const newVpnWithMetrics = { ...vpn, metrics: undefined } as VpnWithMetrics
            const result = prevVpns ? [newVpnWithMetrics, ...prevVpns] : [newVpnWithMetrics]
            logger.debug(`Setting vpnWithMetrics for ${JSON.stringify(result)}`)
            return result
        })
        setLoading(false)
        setLocationMsg(<Text>⏳ VPN in {location.city} is provisioning — see <a href="#your-vpns">Your Vpns</a> above.</Text>)

        logger.log(`Waiting for vpn to be in Running state: ${JSON.stringify(vpn)}`)
        try {
            const runningVpn = await client.waitUntilVpnStateIs(vpn.vpnId, VpnState.Running, 300, (polledVpn: Vpn, pollCount: number) => {
                setVpnsWithMetrics((prevVpns: VpnWithMetrics[]) => {
                    return reactUpdateVpnWithMetrics(prevVpns, {
                        ...polledVpn,
                        metrics: undefined,
                        pollCount
                    })
                })
            })
            logger.log(`Vpn is now in Running state: ${JSON.stringify(runningVpn)}`)

            const response = await client.getVpn(runningVpn.vpnId)
            logger.log(`Vpn metrics: ${JSON.stringify(response)}`)

            setVpnsWithMetrics((prevVpns: VpnWithMetrics[]) => {
                // Time has flied in between, the previous array might contain other 
                // newly created Vpns.
                const result = reactUpdateVpnWithMetrics(prevVpns, {
                    ...response.vpn,
                    metrics: response.metrics
                })
                logger.log(`Setting vpnWithMetrics for ${JSON.stringify(result)}`)
                return result
            })
        } catch (e) {
            logger.error(`Error creating vpn: ${JSON.stringify(e)}`)
            setError(e)
        }
    } catch (e) {
        logger.error(`Error creating vpn: ${JSON.stringify(e)}`)
        setError(e)
        setLoading(false)
    } finally {
        setLocationMsg("")
    }
}

function Action({ client, location, locationMsg, setLocationMsg, config, setVpnsWithMetrics }:
    {
        client: MyTmpVpnClient,
        location: Location,
        locationMsg: React.ReactNode,
        setLocationMsg: any,
        config: VpnConfig,
        setVpnsWithMetrics: any
    }) {

    const { setError } = useErrorContext()
    const [loading, setLoading] = React.useState<boolean>(false)

    return (
        <Button
            size='small'
            isLoading={loading}
            onClick={() => createVpn(
                client,
                location,
                setLocationMsg,
                config,
                setVpnsWithMetrics,
                setError,
                setLoading)}>
            Create
        </Button>
    )
}

function LocationsList({ locations, client, vpnConfig, vpnConfigLimits, setVpnsWithMetrics, navigate, searchTerm, balance }:
    {
        locations: LocationWithProvidersInfo[],
        client: MyTmpVpnClient,
        vpnConfig: VpnConfig,
        vpnConfigLimits: VpnConfigLimits,
        setVpnsWithMetrics: any,
        navigate: NavigateFunction,
        searchTerm: string,
        balance: number
    }) {

    // Filter locations based on search term
    const filteredLocations = locations.filter(location => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        return location.city.toLowerCase().includes(search) ||
            location.country.toLowerCase().includes(search)
    })

    // Group locations by country
    const locationsByCountry = filteredLocations.reduce((acc, location) => {
        if (!acc[location.country]) {
            acc[location.country] = []
        }
        acc[location.country].push(location)
        return acc
    }, {} as Record<string, LocationWithProvidersInfo[]>)

    // Sort countries alphabetically
    const sortedCountries = Object.keys(locationsByCountry).sort()

    return (
        <Table
            highlightOnHover={true}
            variation="striped"
        >
            <TableHead>
                <TableRow>
                    <TableCell as="th">Country</TableCell>
                    <TableCell as="th">City</TableCell>
                    <TableCell as="th">Features</TableCell>
                    {
                        // We only show cost when client is logged in, as cost depends on selected vpn profile.
                        isClientAuthenticated(client) &&
                        <>
                            <TableCell as="th">🥜/mn</TableCell>
                            <TableCell as="th">Est. 🥜</TableCell>
                            <TableCell as="th">Runtime</TableCell>
                            <TableCell as="th">Message</TableCell>
                        </>
                    }
                    <TableCell as="th">Action</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {
                    locations.length > 0 && !Number.isNaN(vpnConfig.deleteAfter) ?
                        sortedCountries.flatMap(country =>
                            locationsByCountry[country]!
                                .sort((a: Location, b: Location) => a.city.localeCompare(b.city))
                                .flatMap((location: LocationWithProvidersInfo, indexLocation: number) => {
                                    const providers = Object.keys(location.providersInfo)
                                    if (providers.length <= 0) {
                                        // Error
                                        return <Alert>`No provider found in ${JSON.stringify(location)}`</Alert>
                                    }
                                    const firstProvider = providers[0]! as InfrastructureProvider
                                    const providerInfo = location.providersInfo[firstProvider]
                                    const pricingRates = providerInfo!.pricingRates
                                    const instanceTypes = Object.keys(pricingRates)
                                    if (instanceTypes.length <= 0) {
                                        // Error
                                        return <Alert>`No instance types found in ${JSON.stringify(location)}`</Alert>
                                    }
                                    return <LocationRow
                                        key={`${location.geonamesId}`}
                                        index={indexLocation}
                                        location={location}
                                        firstProvider={firstProvider}
                                        client={client}
                                        vpnConfig={vpnConfig}
                                        vpnConfigLimits={vpnConfigLimits}
                                        setVpnsWithMetrics={setVpnsWithMetrics}
                                        navigate={navigate}
                                        balance={balance}
                                    />
                                })
                        )
                        :
                        <TableRow>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                            <TableCell><Loader variation='linear' /></TableCell>
                        </TableRow>
                }
            </TableBody>
        </Table>
    )
}


function Specifications({ location, provider }:
    {
        location: LocationWithProvidersInfo,
        provider: InfrastructureProvider
    }) {
    const providerInfo = location.providersInfo[provider]
    const ipv6 = providerInfo!.infraDetails.ipv6Supported

    return <Flex>
        <IPv6Badge supported={ipv6} />
    </Flex>
}

const CrossIcon = ({ size = 12 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
        <line x1="4" y1="4" x2="20" y2="20" stroke="#e53935" strokeWidth="3" strokeLinecap="round" />
        <line x1="20" y1="4" x2="4" y2="20" stroke="#e53935" strokeWidth="3" strokeLinecap="round" />
    </svg>
)

const CheckIcon = ({ size = 12 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
        <rect width="24" height="24" rx="4" fill="#43a047" />
        <polyline points="6,12 10,16 18,8" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

function SupportedIcon({ supported }: { supported: boolean }) {
    return supported ? <CheckIcon /> : <CrossIcon />
}

function IPv6SupportedIcon({ supported }: { supported: boolean }) {
    return <View>
        <SupportedIcon supported={supported} /> IPv6
    </View>
}

function IPv6Badge({ supported }: { supported: boolean }) {
    return (
        <HelpHelper helpMessage={supported ? 'Supports IPv4 and IPv6' : 'Supports only IPv4, IPv6 is not supported'}>
            <IPv6SupportedIcon supported={supported} />
        </HelpHelper >
    )
}

function VpnCostComponent({ pricingRate }: { pricingRate: PricingRate | undefined }) {
    if (!pricingRate) return "—"

    const min = pricingRate.peanutsPerMinuteMin.toFixed(2)
    const max = pricingRate.peanutsPerMinuteMax.toFixed(2)

    return <HelpHelper helpMessage={<>Idle: {min} 🥜/mn · Full speed: {max} 🥜/mn according to the <Link href="#instanceType">selected profile</Link></>}>
        Up to {max}
    </HelpHelper>
}

function VpnEstimatedCostComponent({ pricingRate, vpnConfig }: { pricingRate: PricingRate | undefined, vpnConfig: VpnConfig }) {
    if (!pricingRate || vpnConfig.deleteAfter == null) return "—"

    const est = computeEstimatedCost(vpnConfig.deleteAfter, pricingRate)
    const min = est.min.toFixed(2)
    const max = est.max.toFixed(2)

    return <HelpHelper helpMessage={<>Idle: {min} 🥜 · Full speed: {max} 🥜  over <Link href="#deleteAfter">maximum time allowed</Link> {convertSecondsToHMSShort(vpnConfig.deleteAfter)}</>}>
        Up to {max}
    </HelpHelper>
}

function VpnEstimatedRuntimeComponent({ pricingRate, balance, vpnConfig }:
    {
        pricingRate: PricingRate | undefined,
        balance: number
        vpnConfig: VpnConfig
    }) {

    if (!pricingRate || Number.isNaN(balance)) return "—"

    const actualMaxPeanuts = getConfiguredMaxPeanuts(vpnConfig.maxPeanuts, balance)

    const est = computeEstimatedRuntime(actualMaxPeanuts, pricingRate)
    const min = est.minSeconds
    const max = est.maxSeconds

    return <HelpHelper helpMessage={<>Idle: {convertSecondsToHMSShort(max)} · Full speed: {convertSecondsToHMSShort(min)} over <Link href="#maxPeanuts">maximum peanuts allowed</Link> {actualMaxPeanuts}</>}>
        At least {convertSecondsToHMSShort(min)}
    </HelpHelper>
}



function LocationRow({ index, location, firstProvider, client, vpnConfig, vpnConfigLimits, setVpnsWithMetrics, navigate, balance }:
    {
        index: number,
        location: LocationWithProvidersInfo,
        firstProvider: InfrastructureProvider,
        client: MyTmpVpnClient,
        vpnConfig: VpnConfig,
        vpnConfigLimits: VpnConfigLimits,
        setVpnsWithMetrics: any,
        navigate: NavigateFunction,
        balance: number
    }): React.JSX.Element {

    const [locationMsg, setLocationMsg] = React.useState<React.ReactNode>("")
    const pricingRate = client.getSession() ? location.providersInfo[firstProvider]?.pricingRates[vpnConfig.instanceType!] : undefined

    return <TableRow key={index}>
        <TableCell>
            <Country location={location} />
        </TableCell>
        <TableCell>
            <City location={location} />
        </TableCell>
        <TableCell>
            <Specifications
                location={location}
                provider={firstProvider}
            />
        </TableCell>
        {
            isClientAuthenticated(client) &&
            <>
                <TableCell>
                    <VpnCostComponent pricingRate={pricingRate} />
                </TableCell>
                <TableCell>
                    <VpnEstimatedCostComponent pricingRate={pricingRate} vpnConfig={vpnConfig} />
                </TableCell>
                <TableCell>
                    <VpnEstimatedRuntimeComponent
                        pricingRate={pricingRate}
                        balance={balance}
                        vpnConfig={vpnConfig}
                    />
                </TableCell>
                <TableCell>{locationMsg &&
                    <Alert
                        variation='info'
                        isDismissible={true}
                        fontSize={'0.75rem'}
                        onDismiss={() => setLocationMsg("")}>
                        {locationMsg}
                    </Alert>
                }
                </TableCell>
            </>
        }
        <TableCell>
            {isClientAuthenticated(client) ?
                <Action
                    client={client}
                    location={location}
                    locationMsg={locationMsg}
                    setLocationMsg={setLocationMsg}
                    config={vpnConfig}
                    setVpnsWithMetrics={setVpnsWithMetrics} />
                : <Button variation="link" onClick={() => navigate('/pricing')}>Create</Button>}
        </TableCell>
    </TableRow>
}

/** Compact card for a single location in short/mobile mode */
function LocationCard({ location, client, vpnConfig, setVpnsWithMetrics, pricingRate, navigate }:
    {
        location: LocationWithProvidersInfo,
        client: MyTmpVpnClient,
        vpnConfig: VpnConfig,
        setVpnsWithMetrics: any,
        pricingRate: PricingRate | undefined,
        navigate: NavigateFunction
    }) {
    const [locationMsg, setLocationMsg] = React.useState<React.ReactNode>("")

    return (
        <Card variation="outlined" padding="0.75rem" marginBottom="0.5rem">
            <Flex justifyContent="space-between" alignItems="center">
                <Flex alignItems="center" gap="0.5rem">
                    <Country location={location} />
                    <City location={location} />
                </Flex>
                <Flex alignItems="center" gap="0.5rem">
                    {pricingRate && (
                        <Text fontSize="0.7rem" color="gray">≤{pricingRate.peanutsPerMinuteMax.toFixed(1)}🥜/mn</Text>
                    )}
                    {isClientAuthenticated(client) ?
                        <Action
                            client={client}
                            location={location}
                            locationMsg={locationMsg}
                            setLocationMsg={setLocationMsg}
                            config={vpnConfig}
                            setVpnsWithMetrics={setVpnsWithMetrics} />
                        : <Button size="small" variation="link" onClick={() => navigate('/pricing')}>Create</Button>
                    }
                </Flex>
            </Flex>
            {locationMsg && (
                <Alert variation='info' isDismissible={true} fontSize={'0.7rem'} marginTop="0.25rem"
                    onDismiss={() => setLocationMsg("")}>
                    {locationMsg}
                </Alert>
            )}
        </Card>
    )
}

/** Skeleton placeholder shown while locations are loading */
export function LocationsSkeleton() {
    return (
        <PageContainer>
            <MainTitle>Locations</MainTitle>
            <Flex direction="column" gap="1rem" padding="1rem">
                <Placeholder height="2.5rem" />
                <Placeholder height="3rem" />
                <Placeholder height="3rem" />
                <Placeholder height="3rem" />
                <Placeholder height="3rem" />
                <Placeholder height="3rem" />
            </Flex>
        </PageContainer>
    )
}

export function Locations({ client, vpnConfig, vpnConfigLimits, setVpnsWithMetrics, locations, balance }: {
    client: MyTmpVpnClient,
    vpnConfig: VpnConfig,
    vpnConfigLimits: VpnConfigLimits,
    setVpnsWithMetrics: any,
    locations: LocationWithProvidersInfo[],
    balance: number
}) {

    const [searchTerm, setSearchTerm] = React.useState<string>("")
    const navigate = useNavigate()
    const componentSize = useComponentSize()

    // Filter locations based on search term
    const filteredLocations = locations.filter(location => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        return location.city.toLowerCase().includes(search) ||
            location.country.toLowerCase().includes(search)
    })

    // Group locations by country and sort
    const locationsByCountry = filteredLocations.reduce((acc, location) => {
        if (!acc[location.country]) {
            acc[location.country] = []
        }
        acc[location.country].push(location)
        return acc
    }, {} as Record<string, LocationWithProvidersInfo[]>)
    const sortedCountries = Object.keys(locationsByCountry).sort()

    return (
        <PageContainer>
            <MainTitle>Locations</MainTitle>
            <View marginBottom="1rem">
                <Input
                    placeholder="Search by city or country..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </View>
            {componentSize === 'short' ? (
                <View>
                    {locations.length > 0 && !Number.isNaN(vpnConfig.deleteAfter) ?
                        sortedCountries.flatMap(country =>
                            locationsByCountry[country]!
                                .sort((a: Location, b: Location) => a.city.localeCompare(b.city))
                                .map((location: LocationWithProvidersInfo) => {
                                    const providers = Object.keys(location.providersInfo)
                                    if (providers.length <= 0) return null
                                    const firstProvider = providers[0]! as InfrastructureProvider
                                    const pricingRate = client.getSession() ? location.providersInfo[firstProvider]?.pricingRates[vpnConfig.instanceType!] : undefined
                                    return <LocationCard
                                        key={location.geonamesId}
                                        location={location}
                                        client={client}
                                        vpnConfig={vpnConfig}
                                        setVpnsWithMetrics={setVpnsWithMetrics}
                                        pricingRate={pricingRate}
                                        navigate={navigate}
                                    />
                                })
                        )
                        : <Loader variation='linear' />
                    }
                </View>
            ) : (
                <div className="responsive-table-wrapper">
                    <LocationsList
                        locations={locations}
                        client={client}
                        vpnConfig={vpnConfig}
                        vpnConfigLimits={vpnConfigLimits}
                        setVpnsWithMetrics={setVpnsWithMetrics}
                        navigate={navigate}
                        searchTerm={searchTerm}
                        balance={balance}
                    />
                </div>
            )}
        </PageContainer>
    )
}